import React from "react";
import clsx from "clsx";
import SidebarHeader from "./components/SidebarHeader";
import SidebarFooter from "./components/SidebarFooter";
import Tabs from "components/Tabs";
import { useTranslation } from "react-i18next";
import CurrentOrganisation from "./features/CurrentOrganisation";
import SharedBuildingsList from "./features/SharedBuildingsList";
import { observer } from "mobx-react-lite";
import { sidebarMenuStore } from "./SidebarMenuStore";
import { buildingStore } from "store/Building/BuildingStore";
import { organisationStore } from "features/Organisation/OrganisationStore";
import { useNavigate, useParams } from "react-router";
import { ROUTES_ENUM } from "routes/consts";
import useSidebarMenu from "./useSidebarMenu";

const SidebarMenu = ({
  open,
  toggleSidebar,
}: {
  open: boolean;
  toggleSidebar: () => void;
}) => {
  const { t } = useTranslation();
  const assetLicense = sidebarMenuStore.hasOrganisationAssetLicense();
  const { sharedBuildings } = buildingStore;
  const { organisations } = organisationStore;
  const navigate = useNavigate();
  const { openLastEditedSharedBuilding } = useSidebarMenu();
  const { id: buildingID } = useParams();

  const onChangeTab = (index: number) => {
    if (
      index === 0 &&
      organisations.items.length > 0 &&
      !organisations.loading
    ) {
      navigate(`/${ROUTES_ENUM.DASHBOARD}`);
      buildingStore.setCurrentBuilding(undefined);
      buildingStore.setBuildingID(undefined);
    }
    sidebarMenuStore.setMenuIndex(index);
    if (sidebarMenuStore.hasOrganisationAssetLicense()) {
      navigate(
        `../${sidebarMenuStore.currentOrganisation?.id}/${ROUTES_ENUM.PORTFOLIO_ASSETS}`
      );
    }
  };

  React.useEffect(() => {
    const hasNoOrganisations =
      organisationStore.organisations.items.length === 0 &&
      !organisations.loading;

    if (hasNoOrganisations) {
      sidebarMenuStore.setMenuIndex(1);
      openLastEditedSharedBuilding();
    } else {
      sidebarMenuStore.getCurrentOrgBuildings();
    }
  }, []);

  React.useEffect(() => {
    if (!buildingID) return;
    buildingStore.checkIfUserIsGuest(buildingID);
    if (buildingStore.isSharedBuilding) {
      sidebarMenuStore.setMenuIndex(1);
    }
  }, [buildingStore.sharedBuildings.items.length]);

  function content() {
    return (
      <>
        {sharedBuildings.items.length > 0 ? (
          <Tabs
            containerClassName="!px-0 flex flex-col flex-grow border-b-0"
            panelClassName="border-t border-gray-200 h-full flex flex-col flex-grow mt-[1px]"
            buttonClassName="w-full"
            options={[
              {
                item: t("organisationSettings.myOrganisations"),
                content: <CurrentOrganisation />,
              },
              {
                item: t("shareBuilding.sharedBuildings"),
                content: <SharedBuildingsList />,
              },
            ]}
            currentIndex={sidebarMenuStore.menuIndex}
            onChange={onChangeTab}
          />
        ) : (
          <CurrentOrganisation containerClassName="border-t border-gray-300" />
        )}
      </>
    );
  }

  return (
    <div
      className={clsx(
        "min-h-screen max-h-screen bg-white fixed z-30 flex flex-col",
        "content-between top-0 w-[350px] transition-all border-r border-gray-300",
        { "-ml-[350px]": !open }
      )}
    >
      <SidebarHeader
        toggleSidebar={toggleSidebar}
        license={sidebarMenuStore.getCurrentLicense()}
      />
      {organisations.loading || sharedBuildings.loading
        ? skeleton()
        : content()}
      <SidebarFooter isAsset={assetLicense} />
    </div>
  );
};

export default React.memo(observer(SidebarMenu));

function skeleton() {
  return (
    <div className="animate-pulse p-4 flex flex-grow flex-col border-t border-gray-300">
      <div className="h-[64px] w-full bg-gray-200 rounded-lg dark:bg-gray-700 mb-2.5"></div>
      <div className="h-[40px] w-full bg-gray-200 rounded-lg dark:bg-gray-700 mb-2.5"></div>
      <div className="h-[40px] w-full bg-gray-200 rounded-lg dark:bg-gray-700 mb-2"></div>
      <div className="h-[40px] w-full bg-gray-200 rounded-lg dark:bg-gray-700 mb-2"></div>
      <div className="h-[40px] w-full bg-gray-200 rounded-lg dark:bg-gray-700 mb-1.5"></div>
      <div className="h-[40px] w-full bg-gray-200 rounded-lg dark:bg-gray-700 mb-1.5"></div>
      <div className="h-[40px] w-full bg-gray-200 rounded-lg dark:bg-gray-700 mb-1.5"></div>
    </div>
  );
}
